import type { Dispatch, FC, FocusEvent, SetStateAction } from 'react';

import { serializeDate } from '@/helpers/dateSerializer';
import { Datepicker } from '@/libs/flowbite/Datepicker';
import { TextInput } from 'flowbite-react';
import { memo, useEffect, useState } from 'react';
import { HiCalendar } from 'react-icons/hi';

type IDateRangeProps = {
  className: string;
  placeholder: string;
  defaultRangeStart?: Date;
  defaultRangeEnd?: Date;
  allowOpenEnd?: boolean;
  setParentValue: Dispatch<SetStateAction<string>>;
  onValidationChange?: (isInvalid: boolean) => void;
};

const DateRange: FC<IDateRangeProps> = ({
  className,
  placeholder,
  defaultRangeStart,
  defaultRangeEnd,
  allowOpenEnd,
  setParentValue,
  onValidationChange,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const calcFrom = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  };

  const initialRangeStart = defaultRangeStart ?? calcFrom();
  const initialRangeEnd = defaultRangeEnd ?? new Date();

  const [from, setFrom] = useState(serializeDate(initialRangeStart));
  const [rangeStart, setRangeStart] = useState(initialRangeStart);

  const [to, setTo] = useState(
    allowOpenEnd && !defaultRangeEnd ? '' : serializeDate(initialRangeEnd)
  );
  const [rangeEnd, setRangeEnd] = useState<Date | undefined>(
    allowOpenEnd && !defaultRangeEnd ? undefined : initialRangeEnd
  );

  const value = from ? from + ' - ' + (to ?? '') : '';

  const isInvalidRange =
    rangeEnd !== undefined && rangeStart.getTime() > rangeEnd.getTime();

  useEffect(() => {
    onValidationChange?.(isInvalidRange);
  }, [isInvalidRange, onValidationChange]);

  useEffect(() => {
    if (!defaultRangeEnd && !defaultRangeStart) {
      if (isInvalidRange) {
        setParentValue('');
      } else {
        setParentValue(value);
      }
    }
  }, [defaultRangeEnd, defaultRangeStart, isInvalidRange, value]);

  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsFocused(false);
    }
  };

  return (
    <div
      title="Диапазон дат"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      onFocus={() => setIsFocused(true)}
      onBlur={onBlur}
      className={className + ' relative z-[100]'}
    >
      <TextInput
        icon={HiCalendar}
        readOnly
        value={value || placeholder}
        color={isInvalidRange ? 'failure' : undefined}
        helperText={
          isInvalidRange ? (
            <span className="text-red-600">
              Дата "от" не может быть позже даты "до"
            </span>
          ) : undefined
        }
      />
      {isFocused && (
        <div className="absolute z-[100] mt-2 w-max">
          {allowOpenEnd && (
            <div className="flex w-full items-center gap-2 rounded-t border border-gray-200 bg-white px-3 py-2">
              <button
                type="button"
                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => {
                  setTo('');
                  setRangeEnd(undefined);
                  setParentValue(from + ' - ');
                }}
              >
                Без даты окончания
              </button>
              <button
                type="button"
                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => {
                  const next = new Date(rangeStart);
                  next.setMonth(next.getMonth() + 1);
                  setRangeEnd(next);
                  const str = serializeDate(next);
                  setTo(str);
                  setParentValue(from + ' - ' + str);
                }}
              >
                1 мес
              </button>
              <button
                type="button"
                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => {
                  const endOfMonth = new Date(
                    rangeStart.getFullYear(),
                    rangeStart.getMonth() + 1,
                    0
                  );
                  setRangeEnd(endOfMonth);
                  const str = serializeDate(endOfMonth);
                  setTo(str);
                  setParentValue(from + ' - ' + str);
                }}
              >
                До конца мес
              </button>
              <button
                type="button"
                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => {
                  const endOfYear = new Date(rangeStart.getFullYear(), 11, 31);
                  setRangeEnd(endOfYear);
                  const str = serializeDate(endOfYear);
                  setTo(str);
                  setParentValue(from + ' - ' + str);
                }}
              >
                До конца года
              </button>
            </div>
          )}
          <div className="flex flex-wrap items-start border border-gray-200 bg-white p-2 sm:flex-nowrap">
            <Datepicker
              key={`start-${serializeDate(rangeStart)}`}
              onSelectedDateChanged={(date) => {
                setRangeStart(date);
                setFrom(serializeDate(date));
                const newIsInvalid = rangeEnd !== undefined && date > rangeEnd;
                setParentValue(
                  newIsInvalid ? '' : serializeDate(date) + ' - ' + (to ?? '')
                );
              }}
              weekStart={1}
              language="ru-RU"
              defaultDate={rangeStart}
              rangeStart={to ? rangeStart : undefined}
              rangeEnd={to ? rangeEnd : undefined}
              theme={
                !to
                  ? {
                      views: {
                        days: {
                          items: { item: { inRange: '' } },
                        },
                      },
                    }
                  : undefined
              }
              showTodayButton={false}
              showClearButton={false}
              inline
            />

            <Datepicker
              key={`end-${to || 'open'}`}
              onSelectedDateChanged={(date) => {
                setRangeEnd(date);
                setTo(serializeDate(date));
                const newIsInvalid = rangeStart > date;
                setParentValue(
                  newIsInvalid ? '' : from + ' - ' + serializeDate(date)
                );
              }}
              weekStart={1}
              language="ru-RU"
              defaultDate={rangeEnd ?? rangeStart}
              rangeStart={to ? rangeStart : undefined}
              rangeEnd={to ? rangeEnd : undefined}
              theme={
                !to
                  ? {
                      views: {
                        days: {
                          items: { item: { selected: '', inRange: '' } },
                        },
                      },
                    }
                  : undefined
              }
              showTodayButton={false}
              showClearButton={false}
              inline
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(DateRange);
